import React from "react";

/**
 * Subtle drifting starfield + soft cosmic glow — matches the approved
 * Hostinger <canvas id="cosmos"> layer. Fixed full-viewport, behind .bg-smoke.
 * Honors prefers-reduced-motion.
 */
export default function CosmosCanvas() {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf = 0;
    let stars = [];
    let glows = [];
    let nebulae = [];
    let w = 0, h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function resize() {
      w = window.innerWidth;
      h = window.innerHeight;
      cv.width = Math.floor(w * dpr);
      cv.height = Math.floor(h * dpr);
      cv.style.width = w + "px";
      cv.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // 3D parallax: 3 depth layers — far (dim, slow, small), mid, near (slightly bigger)
      const total = Math.min(950, Math.round((w * h) / 2300));
      const farCount  = Math.round(total * 0.62);
      const midCount  = Math.round(total * 0.30);
      const nearCount = total - farCount - midCount;
      stars = [
        ...new Array(farCount).fill(0).map(() => makeStar(w, h, "far")),
        ...new Array(midCount).fill(0).map(() => makeStar(w, h, "mid")),
        ...new Array(nearCount).fill(0).map(() => makeStar(w, h, "near")),
      ];

      // Mysterious deep-cosmos clouds — barely visible, just adds painted depth (no bright bubbles)
      nebulae = [
        { x: w * 0.20, y: h * 0.35, r: Math.max(w, h) * 0.70, color: "rgba(40, 28, 80, .10)",  vx: 0.012, vy: 0.006 },
        { x: w * 0.78, y: h * 0.55, r: Math.max(w, h) * 0.75, color: "rgba(15, 50, 90, .10)",  vx: -0.010, vy: -0.005 },
        { x: w * 0.50, y: h * 1.05, r: Math.max(w, h) * 0.85, color: "rgba(80, 60, 25, .07)",  vx: 0.008, vy: -0.004 },
      ];

      // Soft accent glows behind product/media zone — reduced so they don't read as orbs
      glows = [
        { x: w * 0.18, y: h * 0.30, r: Math.max(w, h) * 0.42, color: "rgba(201,184,138,.10)", vx: 0.04, vy: 0.02 },
        { x: w * 0.82, y: h * 0.50, r: Math.max(w, h) * 0.46, color: "rgba(125,211,252,.09)", vx: -0.03, vy: -0.025 },
        { x: w * 0.50, y: h * 1.05, r: Math.max(w, h) * 0.48, color: "rgba(125,211,252,.07)", vx: 0.02, vy: -0.015 },
      ];
    }

    function makeStar(W, H, layer) {
      const tier = Math.random();
      let size, alpha, vmul;
      if (layer === "far") {
        size  = 0.4 + Math.random() * 0.4;     // tiny pinpoints
        alpha = 0.30 + Math.random() * 0.30;
        vmul  = 0.35;
      } else if (layer === "mid") {
        size  = 0.6 + Math.random() * 0.5;     // small sharp dots
        alpha = 0.50 + Math.random() * 0.30;
        vmul  = 0.65;
      } else {
        // "near" layer — only a few, slightly bigger but still tight
        size  = tier > 0.92 ? 1.2 + Math.random() * 0.5 : 0.8 + Math.random() * 0.4;
        alpha = 0.65 + Math.random() * 0.25;
        vmul  = 1.1;
      }
      return {
        layer, x: Math.random() * W, y: Math.random() * H, s: size, a: alpha,
        ph: Math.random() * Math.PI * 2,
        sp: 0.0008 + Math.random() * 0.0018,
        vx: (Math.random() - 0.5) * 0.05 * vmul,
        vy: (-0.025 - Math.random() * 0.05) * vmul,
        c: tier > 0.94 ? "#fff3d2" : tier > 0.82 ? "#cfe8ff" : "#ffffff",
      };
    }

    let t = 0;
    function frame() {
      t += 1;
      ctx.clearRect(0, 0, w, h);

      // Layer 1 (deepest): nebula clouds — slow drift, large soft radial gradients
      nebulae.forEach((g) => {
        g.x += g.vx;
        g.y += g.vy;
        if (g.x < -g.r) g.x = w + g.r;
        if (g.x > w + g.r) g.x = -g.r;
        if (g.y < -g.r) g.y = h + g.r;
        if (g.y > h + g.r) g.y = -g.r;
        const grad = ctx.createRadialGradient(g.x, g.y, 0, g.x, g.y, g.r);
        grad.addColorStop(0, g.color);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      });

      // Layer 2: tighter cosmic glows (gold + cyan accents)
      glows.forEach((g) => {
        g.x += g.vx;
        g.y += g.vy;
        if (g.x < -g.r) g.x = w + g.r;
        if (g.x > w + g.r) g.x = -g.r;
        if (g.y < -g.r) g.y = h + g.r;
        if (g.y > h + g.r) g.y = -g.r;
        const grad = ctx.createRadialGradient(g.x, g.y, 0, g.x, g.y, g.r);
        grad.addColorStop(0, g.color);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // Layer 3 (foreground): tiny pinpoint stars — sharp dots, no glow blend (no bubbles)
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        if (!reduce) {
          s.x += s.vx;
          s.y += s.vy;
          s.ph += s.sp * 16;
          if (s.y < -2) { s.y = h + 2; s.x = Math.random() * w; }
          if (s.x < -2) s.x = w + 2;
          if (s.x > w + 2) s.x = -2;
        }
        const tw = 0.7 + Math.sin(s.ph) * 0.3;
        ctx.globalAlpha = Math.max(0, Math.min(1, s.a * tw));
        ctx.fillStyle = s.c;

        // Soft glow ONLY on the brightest near-layer stars — uses shadowBlur (gaussian, no ring artifacts)
        if (s.layer === "near") {
          ctx.shadowColor = s.c;
          ctx.shadowBlur = 6;
        } else if (s.layer === "mid" && s.s > 0.9) {
          ctx.shadowColor = s.c;
          ctx.shadowBlur = 3;
        } else {
          ctx.shadowBlur = 0;
        }

        // Sharp pinpoint pixel — no anti-alias rings/bubbles
        const px = Math.max(1, Math.round(s.s));
        ctx.fillRect(Math.round(s.x), Math.round(s.y), px, px);
      }
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;

      // Layer 4: deep vignette — pulls eyes to center, sells the 3D feel
      const vg = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.85);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,.55)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);

      raf = window.requestAnimationFrame(frame);
    }

    resize();
    window.addEventListener("resize", resize);
    raf = window.requestAnimationFrame(frame);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      id="cosmos"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        opacity: 1,
        pointerEvents: "none",
      }}
    />
  );
}
